import React, { useState, useEffect } from "react";
import { Edit } from "@refinedev/mui";
import {
  Box,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Switch,
  FormControlLabel,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { useTranslate, useNotification, useNavigation } from "@refinedev/core";
import { useParams } from "react-router-dom";
import axiosInstance from "../../api/axios-config";

const PROTOCOL_TYPES = ["HTTP", "TCP_SOCKET", "NONE"] as const;
const MESSAGE_FORMATS = ["JSON", "SIA_DC09", "NONE"] as const;

export const ProviderEdit: React.FC = () => {
  const translate = useTranslate();
  const { open } = useNotification();
  const { list } = useNavigation();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [protocolType, setProtocolType] = useState<string>("HTTP");
  const [messageFormat, setMessageFormat] = useState<string>("JSON");
  const [configJson, setConfigJson] = useState("");
  const [isActive, setIsActive] = useState(true);

  // Company relations
  type Company = { id: number; nom: string };
  const [companies, setCompanies] = useState<Company[]>([]);
  const [operatingCompanies, setOperatingCompanies] = useState<Company[]>([]);
  const [monitoredCompanies, setMonitoredCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  useEffect(() => {
    if (!id) return;

    const fetchProvider = axiosInstance.get("/monitoring/providers/all").then((res) => {
      const provider = (res.data?.data || []).find((p: any) => p.id === parseInt(id, 10));
      if (provider) {
        setCode(provider.code);
        setName(provider.name);
        setProtocolType(provider.protocolType);
        setMessageFormat(provider.messageFormat);
        setIsActive(provider.isActive);
        if (provider.config) {
          setConfigJson(JSON.stringify(provider.config, null, 2));
        }
        if (provider.operatingCompanies) {
          setOperatingCompanies(provider.operatingCompanies);
        }
        if (provider.monitoredCompanies) {
          setMonitoredCompanies(provider.monitoredCompanies);
        }
      }
    }).catch(() => {
      open?.({ type: "error", message: "Erreur lors du chargement" });
    }).finally(() => {
      setLoading(false);
    });

    const fetchCompanies = axiosInstance.get("/company").then((res) => {
      const data = res.data?.data || res.data || [];
      setCompanies(data.map((c: any) => ({ id: c.id, nom: c.nom })));
    }).catch(() => {
      // Non-blocking: companies list is for autocomplete options
    }).finally(() => {
      setLoadingCompanies(false);
    });

    Promise.all([fetchProvider, fetchCompanies]);
  }, [id]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      open?.({ type: "error", message: "Le nom est requis" });
      return;
    }

    let config: Record<string, any> | undefined;
    if (configJson.trim()) {
      try {
        config = JSON.parse(configJson);
      } catch {
        open?.({ type: "error", message: "Config JSON invalide" });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      await axiosInstance.patch(`/monitoring/providers/${id}`, {
        name: name.trim(),
        protocolType,
        messageFormat,
        config,
        isActive,
        operatingCompanyIds: operatingCompanies.map((c) => c.id),
        monitoredCompanyIds: monitoredCompanies.map((c) => c.id),
      });
      open?.({ type: "success", message: "Provider mis à jour" });
      list("telesurveilleur");
    } catch (error: any) {
      open?.({
        type: "error",
        message: error.response?.data?.message || "Erreur lors de la mise à jour",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Edit
      title={`Modifier le provider ${code}`}
      isLoading={isSubmitting}
      saveButtonProps={{ onClick: handleSubmit, disabled: isSubmitting }}
    >
      <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            label="Code"
            value={code}
            disabled
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="Le code ne peut pas être modifié"
          />
          <TextField
            label="Nom"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Protocole</InputLabel>
            <Select
              value={protocolType}
              onChange={(e) => setProtocolType(e.target.value)}
              label="Protocole"
            >
              {PROTOCOL_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Format de message</InputLabel>
            <Select
              value={messageFormat}
              onChange={(e) => setMessageFormat(e.target.value)}
              label="Format de message"
            >
              {MESSAGE_FORMATS.map((f) => (
                <MenuItem key={f} value={f}>{f}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <TextField
          label="Configuration (JSON)"
          value={configJson}
          onChange={(e) => setConfigJson(e.target.value)}
          multiline
          rows={4}
          fullWidth
          placeholder='{ "apiUrl": "https://...", "apiKey": "..." }'
          helperText="Configuration spécifique au provider (optionnel)"
          InputLabelProps={{ shrink: true }}
        />

        <Autocomplete
          multiple
          options={companies}
          value={operatingCompanies}
          onChange={(_, newValue) => setOperatingCompanies(newValue)}
          getOptionLabel={(option) => option.nom}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={loadingCompanies}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Entreprises opératrices"
              helperText="Entreprises qui opèrent en tant que ce provider"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />

        <Autocomplete
          multiple
          options={companies}
          value={monitoredCompanies}
          onChange={(_, newValue) => setMonitoredCompanies(newValue)}
          getOptionLabel={(option) => option.nom}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          loading={loadingCompanies}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Entreprises surveillées"
              helperText="Entreprises surveillées par ce provider"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />

        <FormControlLabel
          control={
            <Switch
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              color="primary"
            />
          }
          label="Actif"
        />
      </Box>
    </Edit>
  );
};
